import { useState, useMemo } from 'react'
import { PaginationState } from '@tanstack/react-table'
import { PaginationUpdateProps } from '../components/ListView/types'

export const DEFAULT_PAGE_SIZE = 50

interface UseListViewPaginationProps {
  defaultPageSize?: number
  defaultPageIndex?: number
  dataLength?: number
  onPaginationChange?: (pagination: PaginationUpdateProps) => Promise<void>
}

export const useListViewPagination = ({
  defaultPageSize = DEFAULT_PAGE_SIZE,
  defaultPageIndex = 0,
  dataLength,
  onPaginationChange,
}: UseListViewPaginationProps = {}) => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: defaultPageIndex,
    pageSize: defaultPageSize,
  })

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  )

  const updatePagination = async ({
    pageIndex: newPageIndex,
    pageSize: newPageSize,
  }: PaginationUpdateProps) => {
    // Calculate the total number of pages
    if (dataLength) {
      const totalPages = Math.ceil(dataLength / newPageSize)
      // Ensure pageIndex doesn't exceed the total number of pages
      if (newPageIndex >= totalPages) {
        newPageIndex = totalPages - 1
      }
    }

    setPagination({ pageIndex: newPageIndex, pageSize: newPageSize })

    if (onPaginationChange) {
      await onPaginationChange({ pageIndex: newPageIndex, pageSize: newPageSize })
    }
  }

  return {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    updatePagination,
  }
}
